import React, { useState } from 'react';
import { updateAgentConfig, initGenome } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  User, Mic, Briefcase, ArrowRight, ArrowLeft, Check, Brain,
  Mail, Phone, Sparkles, Dna
} from 'lucide-react';

const VOICES = [
  { id: 'alloy', name: 'Alloy', desc: 'Neutralny', gender: 'neutral' },
  { id: 'echo', name: 'Echo', desc: 'Ciep\u0142y, m\u0119ski', gender: 'male' },
  { id: 'fable', name: 'Fable', desc: 'Spokojny', gender: 'neutral' },
  { id: 'onyx', name: 'Onyx', desc: 'Autorytarny', gender: 'male' },
  { id: 'nova', name: 'Nova', desc: 'Energiczny', gender: 'female' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Delikatny', gender: 'female' },
];

const ROLE_TEMPLATES = [
  { title: 'Negocjator handlowy', description: 'Prowadzi negocjacje z kontrahentami, ustala warunki um\u00f3w', priority: 'high' },
  { title: 'Analityk rynku', description: 'Bada rynek, wyszukuje nowych dostawc\u00f3w i kontrahent\u00f3w', priority: 'medium' },
  { title: 'Opiekun relacji', description: 'Utrzymuje kontakt z kontrahentami, planuje follow-upy', priority: 'medium' },
  { title: 'Asystent sprzeda\u017cy', description: 'Generuje oferty, raporty, drafty emaili', priority: 'low' },
];

const GENOME_INDUSTRIES = [
  { value: 'oswietlenie', label: 'Oswietlenie', emoji: '💡' },
  { value: 'meble', label: 'Meble', emoji: '🪑' },
  { value: 'budownictwo', label: 'Budownictwo', emoji: '🏗️' },
  { value: 'elektronika', label: 'Elektronika', emoji: '🔌' },
  { value: 'zywnosc', label: 'Zywnosc', emoji: '🍎' },
  { value: 'chemia', label: 'Chemia', emoji: '🧪' },
  { value: 'rolnictwo', label: 'Rolnictwo', emoji: '🌾' },
  { value: 'custom', label: 'Inna branza', emoji: '⚙️' },
];

const STEPS = [
  { key: 'welcome', icon: Brain },
  { key: 'identity', icon: User },
  { key: 'genome', icon: Dna },
  { key: 'contact', icon: Mail },
  { key: 'voice', icon: Mic },
  { key: 'roles', icon: Briefcase },
  { key: 'done', icon: Check },
];

export default function OnboardingWizard({ lang, tenantId, config, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [identity, setIdentity] = useState({
    name: config?.identity?.name || 'Adam',
    bio: config?.identity?.bio || '',
    experience: config?.identity?.experience || '',
    origin: config?.identity?.origin || '',
    default_style: config?.identity?.default_style || 'rzeczowy, ale ciep\u0142y',
    superpower: config?.identity?.superpower || 'czyta mi\u0119dzy wierszami',
    weakness: config?.identity?.weakness || 'nie znosi g\u0142upoty i nieprzygotowania',
    email: config?.identity?.email || '',
    phone: config?.identity?.phone || '',
    avatar_url: config?.identity?.avatar_url || '',
    voice_id: config?.identity?.voice_id || 'onyx',
    voice_speed: config?.identity?.voice_speed || 1.0,
    onboarded: true,
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [genomeIndustry, setGenomeIndustry] = useState('custom');
  const [genomeCustom, setGenomeCustom] = useState('');

  function updateField(field, value) {
    setIdentity(prev => ({ ...prev, [field]: value }));
  }

  function toggleRoleTemplate(idx) {
    setSelectedRoles(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  }

  async function handleFinish() {
    setSaving(true);
    try {
      const roles = selectedRoles.map(idx => ({
        id: `role-${Date.now()}-${idx}`,
        ...ROLE_TEMPLATES[idx],
        active: true,
        created_at: new Date().toISOString()
      }));

      await updateAgentConfig({
        identity,
        beliefs: config?.beliefs,
        roles,
        tasks: config?.tasks || [],
        language: config?.language || 'pl'
      }, tenantId);

      // Initialize Genome with selected industry
      try {
        await initGenome({
          tenant_id: tenantId,
          industry: genomeIndustry,
          industry_custom: genomeCustom || undefined,
          market: 'b2b_krajowy',
          geo_focus: 'PL',
        });
      } catch (genomeErr) {
        console.warn('Genome init during onboarding failed (non-critical):', genomeErr);
      }

      toast.success(lang === 'pl' ? 'Agent skonfigurowany!' : 'Agent configured!');
      onComplete();
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d konfiguracji' : 'Configuration error');
    }
    setSaving(false);
  }

  const canNext = () => {
    if (step === 1) return identity.name.trim().length > 0;
    return true;
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                i < step ? 'bg-primary text-primary-foreground'
                  : i === step ? 'bg-primary/20 text-primary border-2 border-primary'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 ${i < step ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          ))}
        </div>

        <Card className="border border-border shadow-lg">
          <CardContent className="p-8">
            {/* Step 0: Welcome */}
            {step === 0 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto">
                  <Brain className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-bold">
                  {lang === 'pl' ? 'Witaj w \u015awiadomym Negocjatorze!' : 'Welcome to Conscious Negotiator!'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {lang === 'pl'
                    ? 'Skonfigurujmy Twojego agenta AI. Zajmie to oko\u0142o 2 minut.'
                    : "Let's set up your AI agent. This will take about 2 minutes."}
                </p>
              </div>
            )}

            {/* Step 1: Identity */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold">
                  {lang === 'pl' ? 'Jak ma si\u0119 nazywa\u0107 Tw\u00f3j agent?' : 'What should your agent be called?'}
                </h2>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Imi\u0119' : 'Name'} *</Label>
                  <Input
                    value={identity.name}
                    onChange={e => updateField('name', e.target.value)}
                    className="mt-1 text-lg"
                    placeholder="Adam"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Kr\u00f3tki opis / bio' : 'Short bio'}</Label>
                  <Textarea
                    value={identity.bio}
                    onChange={e => updateField('bio', e.target.value)}
                    className="mt-1"
                    rows={2}
                    placeholder={lang === 'pl' ? 'np. Do\u015bwiadczony negocjator handlowy...' : 'e.g. Experienced trade negotiator...'}
                  />
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Do\u015bwiadczenie' : 'Experience'}</Label>
                  <Input
                    value={identity.experience}
                    onChange={e => updateField('experience', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Genome */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Dna className="w-5 h-5 text-primary" />
                  {lang === 'pl' ? 'DNA Twojej branzy' : 'Your Industry DNA'}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {lang === 'pl'
                    ? 'Genome uczy sie z kazdej negocjacji. Wybierz branze, aby MASKIL znal Twoj rynek od startu.'
                    : 'Genome learns from every negotiation. Pick your industry so MASKIL knows your market from day one.'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {GENOME_INDUSTRIES.map(ind => (
                    <button
                      key={ind.value}
                      onClick={() => setGenomeIndustry(ind.value)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        genomeIndustry === ind.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <span className="text-lg mr-2">{ind.emoji}</span>
                      <span className="text-sm font-medium">{ind.label}</span>
                    </button>
                  ))}
                </div>
                {genomeIndustry === 'custom' && (
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Nazwa branzy' : 'Industry name'}</Label>
                    <Input
                      value={genomeCustom}
                      onChange={e => setGenomeCustom(e.target.value)}
                      className="mt-1"
                      placeholder={lang === 'pl' ? 'np. Odziez sportowa, Logistyka...' : 'e.g. Sportswear, Logistics...'}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Contact */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold">
                  {lang === 'pl' ? 'Dane kontaktowe agenta' : 'Agent contact info'}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {lang === 'pl' ? 'Te dane b\u0119d\u0105 u\u017cywane przy wysy\u0142aniu emaili i rozmowach telefonicznych.' : 'Used for sending emails and phone calls.'}
                </p>
                <div>
                  <Label className="text-xs flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email</Label>
                  <Input
                    type="email"
                    value={identity.email}
                    onChange={e => updateField('email', e.target.value)}
                    className="mt-1"
                    placeholder="adam@firma.pl"
                  />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1.5"><Phone className="w-3 h-3" /> {lang === 'pl' ? 'Telefon' : 'Phone'}</Label>
                  <Input
                    type="tel"
                    value={identity.phone}
                    onChange={e => updateField('phone', e.target.value)}
                    className="mt-1"
                    placeholder="+48 123 456 789"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Voice */}
            {step === 4 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold">
                  {lang === 'pl' ? 'Wybierz g\u0142os agenta' : 'Choose agent voice'}
                </h2>
                <div className="grid grid-cols-2 gap-2">
                  {VOICES.map(voice => {
                    const isSelected = identity.voice_id === voice.id;
                    return (
                      <button
                        key={voice.id}
                        onClick={() => updateField('voice_id', voice.id)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border hover:border-border/80'
                        }`}
                      >
                        <span className="text-sm font-medium">{voice.name}</span>
                        <p className="text-xs text-muted-foreground">{voice.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 5: Roles */}
            {step === 5 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold">
                  {lang === 'pl' ? 'Jakie role pe\u0142ni agent?' : 'What roles does the agent have?'}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {lang === 'pl' ? 'Wybierz role lub pomi\u0144 ten krok.' : 'Select roles or skip this step.'}
                </p>
                <div className="space-y-2">
                  {ROLE_TEMPLATES.map((role, i) => {
                    const isSelected = selectedRoles.includes(i);
                    return (
                      <button
                        key={i}
                        onClick={() => toggleRoleTemplate(i)}
                        className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-border/80'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                            isSelected ? 'border-primary bg-primary' : 'border-border'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                          </div>
                          <span className="text-sm font-medium">{role.title}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-7">{role.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 6: Done */}
            {step === 6 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="text-xl font-bold">
                  {lang === 'pl' ? `${identity.name} jest gotowy!` : `${identity.name} is ready!`}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {lang === 'pl'
                    ? 'Tw\u00f3j agent zosta\u0142 skonfigurowany. Mo\u017cesz zmieni\u0107 ustawienia w ka\u017cdej chwili.'
                    : 'Your agent is configured. You can change settings at any time.'}
                </p>
                <div className="bg-muted/50 rounded-xl p-4 text-left space-y-1">
                  <p className="text-sm"><strong>{lang === 'pl' ? 'Imi\u0119:' : 'Name:'}</strong> {identity.name}</p>
                  {identity.email && <p className="text-sm"><strong>Email:</strong> {identity.email}</p>}
                  {identity.phone && <p className="text-sm"><strong>{lang === 'pl' ? 'Telefon:' : 'Phone:'}</strong> {identity.phone}</p>}
                  <p className="text-sm"><strong>{lang === 'pl' ? 'G\u0142os:' : 'Voice:'}</strong> {VOICES.find(v => v.id === identity.voice_id)?.name}</p>
                  {selectedRoles.length > 0 && (
                    <p className="text-sm"><strong>{lang === 'pl' ? 'Role:' : 'Roles:'}</strong> {selectedRoles.map(i => ROLE_TEMPLATES[i].title).join(', ')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              {step > 0 && step < 5 ? (
                <Button variant="ghost" onClick={() => setStep(s => s - 1)} className="gap-1.5">
                  <ArrowLeft className="w-4 h-4" />
                  {lang === 'pl' ? 'Wstecz' : 'Back'}
                </Button>
              ) : <div />}

              {step < 5 ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="gap-1.5">
                  {step === 0
                    ? (lang === 'pl' ? 'Rozpocznij' : 'Get Started')
                    : (lang === 'pl' ? 'Dalej' : 'Next')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button onClick={handleFinish} disabled={saving} className="gap-1.5 w-full">
                  <Sparkles className="w-4 h-4" />
                  {saving
                    ? (lang === 'pl' ? 'Zapisywanie...' : 'Saving...')
                    : (lang === 'pl' ? 'Uruchom agenta' : 'Launch Agent')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
